import React from 'react';
import { Box, styled } from '@mui/material';
import { format } from 'date-fns';
import { CustomContentProps, useSnackbar } from 'notistack';

import CloseMediumSvgIcon from '../../assets/SvgIcon/CloseMediumSvgIcon';
import getAlertIcon, { AlertType } from '../../utils/getAlertIcon';

interface AlertNotificationProps extends CustomContentProps {
  key: string;
  data: {
    title: string;
    description?: string;
    descriptionNode?: React.ReactNode;
    alertType: AlertType;
    timestamp: string;
  };
}

const ContainerSnackbar = styled(Box, {
  shouldForwardProp: (props) => props !== '$isPanic',
})<{ $isPanic: boolean }>`
  width: 450px;
  height: 80px;
  background-color: ${({ theme, $isPanic }) =>
    $isPanic ? theme.color.red.$120 : theme.color.red.$100};
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.25);
  border-radius: 4px;
  color: white;
  display: flex;
  align-items: center;

  .ellipsis {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  & > .info {
    ${({ theme }) => ({ ...theme.externalTypography.body3 })}
    flex: 1;
    min-width: 0;
    & > .title {
      ${({ theme }) => theme.typography.body2}
      font-family: 'Noto Sans';
      font-style: normal;
      font-weight: 700;
      font-size: 16px;
    }
    & > .description {
      font-family: 'Noto Sans';
      font-style: normal;
      font-weight: 400;
      font-size: 14px;
    }
    & > .date {
      & > span {
        margin: 0 0.625rem;
      }
    }
  }

  & .icon {
    width: 64px;
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  & .alert-icon {
    width: 40px;
    height: 40px;
  }

  & .btn-close {
    width: 32px;
    height: 32px;
    align-self: flex-start;
    border-radius: 4px;
    transition: 0.3s;
    cursor: pointer;
    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }
  }
`;

const AlertNotification = React.forwardRef<
  HTMLDivElement,
  AlertNotificationProps
>((props, ref) => {
  const { data, id } = props;
  const { alertType, title, description, descriptionNode, timestamp } = data;
  const { closeSnackbar } = useSnackbar();

  return (
    <ContainerSnackbar
      ref={ref}
      key={id}
      $isPanic={alertType === AlertType.PANIC_BUTTON}
    >
      <div className="icon">
        {getAlertIcon(alertType, true, true, {
          width: '40px',
          height: '40px',
        })}
      </div>
      <div className="info">
        <div className="title ellipsis" title={title}>
          {title}
        </div>
        {description && (
          <div className="description ellipsis" title={description}>
            {descriptionNode}
          </div>
        )}
        <div className="date">
          {`${format(new Date(timestamp), 'HH : mm : ss')}`}
          <span>|</span>
          {`${format(new Date(timestamp), 'yyyy - MM - dd')}`}
        </div>
      </div>
      <div className="btn-close" onClick={() => closeSnackbar(id)}>
        <CloseMediumSvgIcon sx={{ width: '32px', height: '32px' }} />
      </div>
    </ContainerSnackbar>
  );
});

AlertNotification.displayName = 'AlertNotification';

export default AlertNotification;
